import type { ProductContextId } from '../id/ProductsContextId';
import type { IntlShape } from 'react-intl';
import type { ValueOf } from 'type-fest';

import { Match } from 'effect';

export const ProductsOrderBy = {
  BESTSELLERS: 'BESTSELLERS',
  RELEVANCE: 'RELEVANCE',
  CREATED_AT_ASC: 'CREATED_AT_ASC',
  PRICE_ASC: 'PRICE_ASC',
  PRICE_DESC: 'PRICE_DESC',
} as const;

export type ProductsOrderByT = ValueOf<typeof ProductsOrderBy>;

/**
 * Order options offered on category/brand listings. Relevance is search-only.
 */
export const categoryOrderByOptions: ProductsOrderByT[] = [
  ProductsOrderBy.BESTSELLERS,
  ProductsOrderBy.CREATED_AT_ASC,
  ProductsOrderBy.PRICE_ASC,
  ProductsOrderBy.PRICE_DESC,
];

/**
 * Order options offered on the search results page. Bestsellers is excluded
 * (no Elasticsearch popularity signal); relevance is the default.
 */
export const searchOrderByOptions: ProductsOrderByT[] = [
  ProductsOrderBy.RELEVANCE,
  ProductsOrderBy.CREATED_AT_ASC,
  ProductsOrderBy.PRICE_ASC,
  ProductsOrderBy.PRICE_DESC,
];

/**
 * The order applied when no `order` URL param is present. Search defaults to
 * relevance; taxon/brand listings default to bestsellers. Kept in one place so
 * the SSR fetch, the atom-hydration seed, and the client default agree.
 */
export const defaultOrderByForContext = (context: ProductContextId | null): ProductsOrderByT =>
  context?.kind === 'search' ? ProductsOrderBy.RELEVANCE : ProductsOrderBy.BESTSELLERS;

export function intlProductsOrderBy(intl: IntlShape) {
  return (orderBy: ProductsOrderByT) => {
    return Match.value(orderBy).pipe(
      Match.when(ProductsOrderBy.BESTSELLERS, () =>
        intl.formatMessage({
          id: 'category.products.orderBy.bestsellers',
          defaultMessage: 'Nejprodávanější',
        })
      ),
      Match.when(ProductsOrderBy.RELEVANCE, () =>
        intl.formatMessage({
          id: 'search.products.orderBy.relevance',
          defaultMessage: 'Nejrelevantnější',
        })
      ),
      Match.when(ProductsOrderBy.CREATED_AT_ASC, () =>
        intl.formatMessage({
          id: 'category.products.orderBy.createdAt.asc',
          defaultMessage: 'Nejnovější',
        })
      ),
      Match.when(ProductsOrderBy.PRICE_ASC, () =>
        intl.formatMessage({
          id: 'category.products.orderBy.price.asc',
          defaultMessage: 'Od nejlevnějšího',
        })
      ),
      Match.when(ProductsOrderBy.PRICE_DESC, () =>
        intl.formatMessage({
          id: 'category.products.orderBy.price.desc',
          defaultMessage: 'Od nejdražšího',
        })
      ),
      Match.exhaustive
    );
  };
}
